import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { UiFlowKey } from "@signalist/shared-types";

import { useAppSelector } from "@/redux/hooks";

import { useGetSeenUiFlowsQuery } from "@/pages/Private/redux/userUiFlow/userUiFlow.api";
import { userUiFlowSelector } from "@/pages/Private/redux/userUiFlow/userUiFlow.slice";

import { OutreachExplainerModal } from "./OutreachExplainerModal";

interface OutreachSubHeaderProps {
	/** The list being edited — threaded to the explainer's "Enrich only" shortcut. */
	listId: number | string;
}

/**
 * The line under the stepper on the Outreach step: the one-sentence difference between the two send
 * paths plus the *How outreach works* link that opens the explainer (PREC-3687 §4.0, PREC-3879).
 * The explainer auto-opens on every visit to the step until the user dismisses it for good with its
 * "Don't show this again" checkbox (persisted as a `user_ui_flow` marker); the link keeps working
 * either way.
 */
export const OutreachSubHeader: FunctionComponent<OutreachSubHeaderProps> = ({ listId }) => {
	const { t } = useTranslation();
	const [isExplainerOpened, setIsExplainerOpened] = useState<boolean>(false);

	useGetSeenUiFlowsQuery();
	const { seenKeys, loaded } = useAppSelector(userUiFlowSelector);

	// Latch the auto-open decision on the first loaded read: dismissing inside the modal updates
	// `seenKeys`, and without the latch a close-without-dismiss would re-trigger the effect.
	const hasEvaluatedAutoOpenRef = useRef(false);

	useEffect(() => {
		if (!loaded || hasEvaluatedAutoOpenRef.current) {
			return;
		}

		hasEvaluatedAutoOpenRef.current = true;

		if (!seenKeys.includes(UiFlowKey.OUTREACH_EXPLAINER)) {
			setIsExplainerOpened(true);
		}
	}, [loaded, seenKeys]);

	return (
		<div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-text-meta">
			<span>{t("list.outreach.subHeader")}</span>
			<button
				className="cursor-pointer font-semibold text-brand-light underline underline-offset-2 hover:no-underline"
				type="button"
				onClick={() => setIsExplainerOpened(true)}
			>
				{t("list.outreach.howOutreachWorks")}
			</button>
			{isExplainerOpened && (
				<OutreachExplainerModal
					isOpened
					listId={listId}
					onClose={() => setIsExplainerOpened(false)}
				/>
			)}
		</div>
	);
};
